import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';
import analyticsService from './services/analytics.service';
import { getVisitorIp, detectBot } from './utils/ipTracker';
import EasterAppPromoPopup from './components/promotions/EasterAppPromoPopup';
import { shouldShowAppPromotion } from './utils/deviceDetection';

// Import custom styles
import './styles/product-slider.css';
// Firebase is initialized in firebase/config.js
import MainLayout from './components/layout/MainLayout';
import AdminLayout from './components/layouts/AdminLayout';
import withAdminPassword from './components/admin/withAdminPassword';

// PERFORMANCE OPTIMIZATION: Enhanced loading components with priority-based rendering

// Priority loading system - different loaders for different component types
const LoaderTypes = {
  MINIMAL: 'minimal',     // Ultra-light, just enough to prevent layout shift
  CRITICAL: 'critical',   // For critical path components (home, category pages)
  STANDARD: 'standard',   // Default for most pages
  CONTENT: 'content',     // For content-heavy pages
  PRODUCT: 'product'      // Specific to product displays
};

// Minimal loader - used for highest priority routes to minimize initial load time
const MinimalLoader = () => (
  <div className="w-full animate-pulse">
    <div className="h-16 bg-gray-100 rounded-md mb-4"></div>
    <div className="h-64 bg-gray-100 rounded-md"></div>
  </div>
);

// Critical path loader - slightly more detailed but still very lightweight
const CriticalLoader = () => (
  <div className="w-full animate-pulse">
    <div className="h-16 bg-gray-200 rounded-md mb-4"></div>
    <div className="grid grid-cols-2 gap-4 mb-4">
      <div className="h-48 bg-gray-200 rounded-md"></div>
      <div className="h-48 bg-gray-200 rounded-md"></div>
    </div>
    <div className="h-32 bg-gray-200 rounded-md"></div>
  </div>
);

// Standard page loader - more detailed for regular pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-full max-w-7xl mx-auto px-4">
      <div className="animate-pulse">
        {/* Header skeleton */}
        <div className="h-16 bg-gray-200 rounded-md mb-8"></div>
        
        {/* Content skeleton - resembles actual page layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="h-64 bg-gray-200 rounded-md mb-6"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 bg-gray-200 rounded-md"></div>
              ))}
            </div>
          </div>
          <div className="md:col-span-1">
            <div className="h-96 bg-gray-200 rounded-md"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Product-specific loader
const ProductLoader = () => (
  <div className="animate-pulse">
    <div className="h-64 bg-gray-200 rounded-md mb-4"></div>
    <div className="h-8 bg-gray-200 rounded-md mb-2 w-3/4"></div>
    <div className="h-6 bg-gray-200 rounded-md w-1/2"></div>
  </div>
);

// Smart loader that selects the appropriate loader based on route or component type
const SmartSuspense = ({ children, fallbackType = LoaderTypes.STANDARD }) => {
  let fallback;
  
  switch (fallbackType) {
    case LoaderTypes.MINIMAL:
      fallback = <MinimalLoader />;
      break;
    case LoaderTypes.CRITICAL:
      fallback = <CriticalLoader />;
      break;
    case LoaderTypes.CONTENT:
      fallback = <PageLoader />;
      break;
    case LoaderTypes.PRODUCT:
      fallback = <ProductLoader />;
      break;
    default:
      fallback = <PageLoader />;
  }
  
  return <Suspense fallback={fallback}>{children}</Suspense>;
};

// PERFORMANCE OPTIMIZATION: Implement advanced code splitting with prioritization
// PRIORITY 1: Critical path components - loaded immediately with highest priority
const Home = lazy(() => import(/* webpackPrefetch: true, webpackChunkName: "home" */ './pages/Home'));

// Function to create optimized lazy components with priority loading
const createOptimizedComponent = (importFunc, priority) => {
  if (priority === 'critical') {
    // For critical components: preload immediately
    const modulePromise = importFunc();
    return lazy(() => modulePromise);
  } else if (priority === 'high') {
    // For high priority: prefetch soon after page load
    return lazy(() => {
      const modulePromise = importFunc();
      return modulePromise;
    });
  } else {
    // For normal priority: standard lazy loading
    return lazy(importFunc);
  }
};

// PRIORITY 2: High priority components - loaded soon after initial render
const CategoryList = createOptimizedComponent(
  () => import(/* webpackPrefetch: true, webpackChunkName: "category-list" */ './pages/CategoryList'),
  'high'
);
const CategoryProducts = createOptimizedComponent(
  () => import(/* webpackPrefetch: true, webpackChunkName: "category-products" */ './pages/CategoryProducts'),
  'high'
);
const AllProducts = createOptimizedComponent(
  () => import(/* webpackPrefetch: true, webpackChunkName: "all-products" */ './pages/AllProducts'),
  'high'
);

// PRIORITY 3: Authentication components - medium priority, user might need these soon
const Login = createOptimizedComponent(
  () => import(/* webpackChunkName: "auth" */ './pages/Login'),
  'high'
);
const Register = createOptimizedComponent(
  () => import(/* webpackChunkName: "auth" */ './pages/Register'),
  'high'
);
const ForgotPassword = lazy(() => import(/* webpackChunkName: "auth" */ './pages/ForgotPassword'));
const ResetPassword = lazy(() => import(/* webpackChunkName: "auth" */ './pages/ResetPassword'));

// PRIORITY 4: Shopping flow components - important for conversion
const Cart = createOptimizedComponent(
  () => import(/* webpackPrefetch: true, webpackChunkName: "shopping" */ './pages/Cart'),
  'high'
);
const ProductDetail = createOptimizedComponent(
  () => import(/* webpackChunkName: "shopping" */ './pages/ProductDetail'),
  'high'
);
const Checkout = lazy(() => import(/* webpackChunkName: "shopping" */ './pages/Checkout'));

// PRIORITY 5: Order-related components - load after checkout flow
const OrderSuccess = lazy(() => import(/* webpackChunkName: "orders" */ './pages/OrderSuccess'));
const OrderConfirmation = lazy(() => import(/* webpackChunkName: "orders" */ './pages/OrderConfirmation'));
const MyOrders = lazy(() => import(/* webpackChunkName: "orders" */ './pages/MyOrders'));
const OrderDetail = lazy(() => import(/* webpackChunkName: "orders" */ './pages/OrderDetail'));

// PRIORITY 6: User account components - load when needed
const Profile = lazy(() => import(/* webpackChunkName: "account" */ './pages/Profile'));

// PRIORITY 7: Legal pages - very low priority, rarely accessed
const Terms = lazy(() => import(/* webpackChunkName: "legal" */ './pages/Terms'));
const Privacy = lazy(() => import(/* webpackChunkName: "legal" */ './pages/Privacy'));
const Refund = lazy(() => import(/* webpackChunkName: "legal" */ './pages/Refund'));
const Help = lazy(() => import(/* webpackChunkName: "legal" */ './pages/Help'));
const About = lazy(() => import(/* webpackChunkName: "legal" */ './pages/About'));

// PRIORITY 8: Lowest priority components - load only when explicitly needed
const PaymentCallback = lazy(() => import(/* webpackChunkName: "payment" */ './pages/PaymentCallback'));
const ImageUploadDemo = lazy(() => import(/* webpackChunkName: "debug" */ './pages/ImageUploadDemo'));
const Stores = lazy(() => import(/* webpackChunkName: "stores" */ './pages/Stores'));

// Location pages - SEO optimized landing pages
const Locations = lazy(() => import(/* webpackChunkName: "locations" */ './pages/Locations'));
const LocationPage = lazy(() => import(/* webpackChunkName: "locations" */ './pages/LocationPage'));

// Brand collection pages - SEO topical authority
const BrandListing = createOptimizedComponent(
  () => import(/* webpackPrefetch: true, webpackChunkName: "brands" */ './pages/BrandListing'),
  'high'
);
const BrandProducts = createOptimizedComponent(
  () => import(/* webpackChunkName: "brands" */ './pages/BrandProducts'),
  'high'
);

// Admin pages - grouped by feature with optimized loading
const AdminPages = {
  // Higher priority admin pages - PROTECTED with password
  Dashboard: withAdminPassword(createOptimizedComponent(
    () => import(/* webpackChunkName: "admin-core" */ './pages/admin/Dashboard'),
    'high'
  ), { title: "Dashboard Access Required" }),
  
  // Medium priority admin pages
  Products: lazy(() => import(/* webpackChunkName: "admin-products" */ './pages/admin/Products')),
  Categories: lazy(() => import(/* webpackChunkName: "admin-products" */ './pages/admin/Categories')),
  
  // PROTECTED admin pages - Orders and Users require password
  Orders: withAdminPassword(lazy(() => import(/* webpackChunkName: "admin-orders" */ './pages/admin/Orders')), { 
    title: "Orders Access Required" 
  }),
  
  // Lower priority admin pages
  ExpiredOrders: lazy(() => import(/* webpackChunkName: "admin-orders" */ './pages/admin/ExpiredOrders')),
  Users: withAdminPassword(lazy(() => import(/* webpackChunkName: "admin-users" */ './pages/admin/Users')), { 
    title: "Customer Data Access Required" 
  }),
  StoreAddresses: lazy(() => import(/* webpackChunkName: "admin-settings" */ './pages/admin/StoreAddresses')),
  Banners: lazy(() => import(/* webpackChunkName: "admin-marketing" */ './pages/admin/Banners')),
  Coupons: withAdminPassword(lazy(() => import(/* webpackChunkName: "admin-marketing" */ './pages/admin/Coupons')), { 
    title: "Coupons Management Access Required" 
  }),
  Settings: withAdminPassword(lazy(() => import(/* webpackChunkName: "admin-settings" */ './pages/admin/Settings')), { 
    title: "Settings Access Required" 
  }),
  SpinPrizes: lazy(() => import(/* webpackChunkName: "admin-marketing" */ './pages/admin/SpinPrizes')),
  AdCampaigns: lazy(() => import(/* webpackChunkName: "admin-marketing" */ './pages/admin/AdCampaigns')),
  PageAnalytics: lazy(() => import(/* webpackChunkName: "admin-analytics" */ './pages/admin/PageAnalytics')),
  CouponBanners: lazy(() => import(/* webpackChunkName: "admin-marketing" */ './pages/admin/CouponBanners')),
  AppVersions: lazy(() => import(/* webpackChunkName: "admin-settings" */ './pages/admin/AppVersions')),
  PushNotifications: withAdminPassword(lazy(() => import(/* webpackChunkName: "admin-marketing" */ './pages/admin/PushNotifications')), { 
    title: "Push Notifications Access Required" 
  }),
  Campaigns: withAdminPassword(lazy(() => import(/* webpackChunkName: "admin-marketing" */ './pages/admin/Campaigns')), { 
    title: "Campaigns Access Required" 
  }),
  CampaignTemplates: withAdminPassword(lazy(() => import(/* webpackChunkName: "admin-marketing" */ './pages/admin/CampaignTemplates')), { 
    title: "Campaign Templates Access Required" 
  }),
  EmailAutomation: withAdminPassword(lazy(() => import(/* webpackChunkName: "admin-automation" */ './pages/admin/EmailAutomation')), { 
    title: "Email Automation Access Required" 
  }),
};

// Import pages (to be created)
const NotFound = () => <div className="container mx-auto px-4 py-8">Page Not Found</div>;

// Easter Popup Manager Component - handles homepage detection and popup logic
const EasterPopupManager = () => {
  const [showAppPromo, setShowAppPromo] = useState(false);
  const location = useLocation();

  // Check if Easter app promotion should be shown - only on homepage
  useEffect(() => {
    const checkAppPromotion = () => {
      // Only show on homepage
      const isHomePage = location.pathname === '/' || location.pathname === '/home';
      
      if (isHomePage) {
        // Delay showing popup by 2 seconds to let page load first
        const timer = setTimeout(() => {
          if (shouldShowAppPromotion()) {
            setShowAppPromo(true);
          }
        }, 2000);
        
        return () => clearTimeout(timer);
      }
    };
    
    checkAppPromotion();
  }, [location.pathname]);

  return (
    <EasterAppPromoPopup 
      isVisible={showAppPromo}
      onClose={() => setShowAppPromo(false)}
    />
  );
};

// ...
function App() {
  // Log visitor IP when the app loads, but only on the homepage
  useEffect(() => {
    const logVisitorData = async () => {
      try {
        // Only log visitors on the homepage
        const isHomePage = window.location.pathname === '/' || window.location.pathname === '/home';
        
        if (isHomePage) {
          console.log('Logging visitor on homepage');
          // Get visitor IP and browser information
          const visitorData = await getVisitorIp();
          
          // Check if visitor might be a bot
          const botDetection = detectBot(visitorData);
          
          // Log visitor data with bot detection results
          await analyticsService.logVisitorIp({
            ...visitorData,
            botDetection,
            pageUrl: window.location.href,
            pageTitle: document.title,
            timestamp: new Date().toISOString(),
            isHomePage: true
          });
          
          // If likely bot, log additional information
          if (botDetection.isLikelyBot) {
            console.log('Potential bot detected:', botDetection);
          }
        }
      } catch (error) {
        console.error('Failed to log visitor data:', error);
      }
    };
    
    // OPTIMIZATION: Defer analytics to run AFTER UI renders (prevents blocking initial render)
    setTimeout(() => {
      logVisitorData();
    }, 1000); // Run 1 second after UI appears
    
    // Set up ad click tracking
    const setupAdTracking = () => {
      // Find all ad elements (you may need to adjust the selector based on your ad implementation)
      const adElements = document.querySelectorAll('[data-ad-id]');
      
      adElements.forEach(ad => {
        ad.addEventListener('click', async () => {
          try {
            const adId = ad.getAttribute('data-ad-id');
            const visitorData = await getVisitorIp();
            
            // Log the ad click with visitor information
            await analyticsService.logAdInteraction(adId, 'click', {
              ip: visitorData.ip,
              userAgent: visitorData.userAgent,
              timestamp: new Date().toISOString(),
              pageUrl: window.location.href
            });
          } catch (error) {
            console.error('Failed to log ad interaction:', error);
          }
        });
      });
    };
    
    // Set up ad tracking after a short delay to ensure DOM is fully loaded
    const trackingTimeout = setTimeout(setupAdTracking, 1000);
    
    return () => {
      clearTimeout(trackingTimeout);
    };
  }, []);
  
  return (
    <>
      <Router>
        <AuthProvider>
          <NotificationProvider>
            <ToastProvider>
              <CartProvider>
              {/* Easter Popup Manager - Renders at root level outside MainLayout */}
              <EasterPopupManager />
              <Routes>
                {/* Policy Routes */}
                <Route path="/terms" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Terms />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/privacy" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Privacy />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/refund" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Refund />
                    </Suspense>
                  </MainLayout>
                } />
                {/* Admin Routes */}
                <Route path="/admin" element={<Navigate to="/admin/products" replace />} />
                <Route path="/admin/dashboard" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.Dashboard />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/products" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.Products />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/categories" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.Categories />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/orders" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.Orders />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/expired-orders" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.ExpiredOrders />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/users" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.Users />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/store-addresses" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.StoreAddresses />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/banners" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.Banners />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/coupons" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.Coupons />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/settings" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.Settings />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/spin-prizes" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.SpinPrizes />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/ad-campaigns" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.AdCampaigns />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/page-analytics" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.PageAnalytics />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/coupon-banners" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.CouponBanners />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/app-versions" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.AppVersions />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/push-notifications" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.PushNotifications />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/campaigns" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.Campaigns />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/campaign-templates" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.CampaignTemplates />
                    </Suspense>
                  </AdminLayout>
                } />
                <Route path="/admin/email-automation" element={
                  <AdminLayout>
                    <Suspense fallback={<PageLoader />}>
                      <AdminPages.EmailAutomation />
                    </Suspense>
                  </AdminLayout>
                } />
                
                {/* Critical path routes - optimized for fastest initial load */}
                <Route path="/" element={
                  <MainLayout>
                    {/* Home page gets highest priority with minimal fallback for faster perceived load */}
                    <SmartSuspense fallbackType={LoaderTypes.MINIMAL}>
                      <Home />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/login" element={
                  <SmartSuspense fallbackType={LoaderTypes.CRITICAL}>
                    <Login />
                  </SmartSuspense>
                } />
                <Route path="/register" element={
                  <SmartSuspense fallbackType={LoaderTypes.CRITICAL}>
                    <Register />
                  </SmartSuspense>
                } />
                <Route path="/forgot-password" element={
                  <Suspense fallback={<PageLoader />}>
                    <ForgotPassword />
                  </Suspense>
                } />
                <Route path="/reset-password/:token" element={
                  <Suspense fallback={<PageLoader />}>
                    <ResetPassword />
                  </Suspense>
                } />
                <Route path="/products" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.CRITICAL}>
                      <AllProducts />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/products/:slug" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.PRODUCT}>
                      <ProductDetail />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/categories" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.CRITICAL}>
                      <CategoryList />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/categories/:categorySlug" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.CRITICAL}>
                      <CategoryProducts />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/category/:categorySlug" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.CRITICAL}>
                      <CategoryProducts />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/product/:productSlug" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.PRODUCT}>
                      <ProductDetail />
                    </SmartSuspense>
                  </MainLayout>
                } />
                
                {/* Brand collection routes for SEO topical authority */}
                <Route path="/brands" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.CRITICAL}>
                      <BrandListing />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/brands/:brandSlug" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.CRITICAL}>
                      <BrandProducts />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/cart" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.CRITICAL}>
                      <Cart />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/checkout" element={
                  <MainLayout>
                    <SmartSuspense fallbackType={LoaderTypes.STANDARD}>
                      <Checkout />
                    </SmartSuspense>
                  </MainLayout>
                } />
                <Route path="/order-success" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <OrderSuccess />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/order-confirmation/:id" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <OrderConfirmation />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/my-orders" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <MyOrders />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/orders" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <MyOrders />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/orders/:orderNumber" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <OrderDetail />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/order-confirmation/:orderNumber" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <OrderConfirmation />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/profile" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Profile />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/my-profile" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Profile />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/payment/callback" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <PaymentCallback />
                    </Suspense>
                  </MainLayout>
                } />
                
                <Route path="/image-upload" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <ImageUploadDemo />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/stores" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Stores />
                    </Suspense>
                  </MainLayout>
                } />
                {/* Location Pages - SEO optimized */}
                <Route path="/locations" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Locations />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/locations/:slug" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <LocationPage />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/help" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Help />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="/about" element={
                  <MainLayout>
                    <Suspense fallback={<PageLoader />}>
                      <About />
                    </Suspense>
                  </MainLayout>
                } />
                <Route path="*" element={
                  <MainLayout>
                    <NotFound />
                  </MainLayout>
                } />
              </Routes>
              <ToastContainer position="bottom-right" autoClose={5000} />
            </CartProvider>
            </ToastProvider>
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </>
  );
}

export default App;